import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Guideline {
  id: number;
  title: string;
  organisation: string;
  category: string;
  summary: string;
  fullText: string | null;
  tags: string[];
  url: string | null;
  updatedAt: string;
  createdAt: string;
}

type GForm = Omit<Guideline, "id" | "createdAt" | "updatedAt">;

const EMPTY: GForm = {
  title: "",
  organisation: "",
  category: "",
  summary: "",
  fullText: "",
  tags: [],
  url: "",
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? "Request failed"); }
  return res.json();
}

function GForm({ value, onChange }: { value: GForm; onChange: (v: GForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  const arrStr = (a: string[]) => a.join(", ");
  const strArr = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
      <div>
        <Label>Title *</Label>
        <Input value={value.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. NICE RA Management 2023" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Organisation *</Label>
          <Input value={value.organisation} onChange={(e) => set("organisation", e.target.value)} placeholder="e.g. NICE" />
        </div>
        <div>
          <Label>Category *</Label>
          <Input value={value.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Rheumatoid Arthritis" />
        </div>
      </div>
      <div>
        <Label>Summary *</Label>
        <Textarea rows={3} value={value.summary} onChange={(e) => set("summary", e.target.value)} />
      </div>
      <div>
        <Label>Full text</Label>
        <Textarea rows={5} value={value.fullText ?? ""} onChange={(e) => set("fullText", e.target.value)} />
      </div>
      <div>
        <Label>Tags (comma-separated)</Label>
        <Input value={arrStr(value.tags)} onChange={(e) => set("tags", strArr(e.target.value))} placeholder="RA, DMARDs, monitoring" />
      </div>
      <div>
        <Label>URL</Label>
        <Input value={value.url ?? ""} onChange={(e) => set("url", e.target.value)} type="url" placeholder="https://www.nice.org.uk/…" />
      </div>
    </div>
  );
}

export default function AdminGuidelines() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Guideline | null>(null);
  const [form, setForm] = useState<GForm>(EMPTY);

  const { data: guidelines = [], isLoading } = useQuery<Guideline[]>({
    queryKey: ["admin", "guidelines"],
    queryFn: () => apiFetch("/api/admin/content/guidelines"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: GForm) =>
      editing
        ? apiFetch(`/api/admin/content/guidelines/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : apiFetch("/api/admin/content/guidelines", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "guidelines"] }); setDialogOpen(false); toast({ title: editing ? "Guideline updated" : "Guideline created" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/guidelines/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "guidelines"] }); toast({ title: "Guideline deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(g: Guideline) {
    setEditing(g);
    setForm({ title: g.title, organisation: g.organisation, category: g.category, summary: g.summary, fullText: g.fullText, tags: g.tags, url: g.url });
    setDialogOpen(true);
  }

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Guidelines"
        description={`${guidelines.length} guideline${guidelines.length !== 1 ? "s" : ""}`}
        action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New guideline</Button>}
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Title", "Organisation", "Category", "Actions"]}
          rows={guidelines.map((g) => [
            <div key="title">
              <p className="font-medium text-slate-900 line-clamp-1">{g.title}</p>
              {g.url && <a href={g.url} target="_blank" rel="noopener noreferrer" className="text-xs text-teal-600 flex items-center gap-1 hover:underline"><ExternalLink className="h-3 w-3" />View</a>}
            </div>,
            <Badge key="org" variant="outline" className="text-xs">{g.organisation}</Badge>,
            <span key="cat" className="text-sm text-slate-600">{g.category}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(g)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(g.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit guideline" : "New guideline"}</DialogTitle></DialogHeader>
          <GForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete guideline?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the guideline.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
